import React from "react";
import { filter, indexOf, map } from "lodash";
import styled from "styled-components";
import { content } from "@glossy";
import { PeriodRange } from "@linkfluence/models/date";
import { natural } from "@tootools/sort";
import Panel from "@ui-kit/popover/panel";
import Biglist from "@radarly/components/filterbar/panels/biglist";
import { useFilterbarChoices } from "@radarly/core/filters/use-cases";
import { useCurrentUserLocale } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { Clause } from "@radarly/models/clause";
import { isClauseEmpty } from "@radarly/models/clause/is";
import { refineClauseWithInnerClause } from "@radarly/models/clause/refiner";
import { transformClauseFromField } from "@radarly/models/clause/transformer";
import { FullFacet, isCustomField } from "@radarly/models/facets";
import {
  canExcludeValues,
  getGlobalSelectors,
  getPopularChoices,
} from "@radarly/models/filters";
import { Choice, Status } from "@radarly/models/filters";
import {
  getNextClauseFromStatusAndValue,
  getNextClauseFromStatusAndValues,
} from "./clause";
import InnerPanel from "./inner-panel";

interface IProps {
  clause: Clause;
  facet: FullFacet;
  setClause: (clause: Clause) => void;
  hasTitle?: boolean;
  onApply: () => void;
  onCancel?: () => void;
  period: PeriodRange;
  validateLabel?: string;
  className?: string;
}

const Description = styled.div`
  margin-bottom: 24px;
  color: ${(props) => content(props, "secondary")};
`;

const RadarlyFilterbarPanelsBiglist = ({
  setClause,
  clause,
  facet,
  hasTitle,
  onApply,
  onCancel,
  period,
  validateLabel,
  className,
}: IProps) => {
  const locale = useCurrentUserLocale();
  const { choices, orderedChoices, loading, error } = useFilterbarChoices(
    clause,
    facet,
    period
  );
  const baseChoices = natural(choices, "label");
  const onStatusChange = (choice: Choice, status: Status) => {
    setClause(
      getNextClauseFromStatusAndValue(clause, facet, choice.id, status)
    );
  };

  const onGlobalStatusChange = (status: Status) => {
    setClause(
      getNextClauseFromStatusAndValues(
        clause,
        facet,
        map(choices, "id"),
        status
      )
    );
  };

  const onClear = () => {
    setClause(
      refineClauseWithInnerClause(
        clause,
        transformClauseFromField(
          {
            [facet.id]: [],
          },
          facet.custom ? [facet.id] : []
        )
      )
    );
  };

  return (
    <Panel
      locale={locale}
      onClear={onClear}
      hasTitle={hasTitle}
      title={tr(locale, "FILTERBAR::PANEL::FILTERBY", {
        filter: facet.label,
      })}
      size={choices.length < 5 ? "s" : "m"}
      onApply={onApply}
      onCancel={onCancel}
      disabled={onCancel ? isClauseEmpty(clause) : false}
      validateLabel={validateLabel}
      className={className}
      maxHeight={312}
    >
      {facet.description && <Description>{facet.description}</Description>}
      <InnerPanel loading={loading} error={error} locale={locale}>
        <Biglist
          choices={baseChoices}
          orderedChoices={orderedChoices}
          allowExclusion={canExcludeValues(facet.id)}
          onSelect={onStatusChange}
          onGlobalSelect={onGlobalStatusChange}
          globalSelectors={getGlobalSelectors(facet.id)}
          popularChoices={filter(
            baseChoices,
            (choice) => indexOf(getPopularChoices(facet.id), choice.id) !== -1
          )}
          placeholder={tr(
            locale,
            isCustomField(facet)
              ? "FILTER_PANEL::SEARCH_PLACEHOLDER::SEARCH_VALUE"
              : "FILTER_PANEL::SEARCH_PLACEHOLDER::" + facet.id
          )}
          locale={locale}
        />
      </InnerPanel>
    </Panel>
  );
};

export default RadarlyFilterbarPanelsBiglist;
